import { StoreWithDeployStatus } from '@/models/operations/types'

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export async function runWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = []
  let index = 0

  async function runWorker() {
    while (index < items.length) {
      const currentIndex = index
      index += 1
      try {
        const value = await worker(items[currentIndex])
        results[currentIndex] = { status: 'fulfilled', value }
      } catch (reason) {
        results[currentIndex] = { status: 'rejected', reason }
      }
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => runWorker())
  await Promise.all(workers)
  return results
}

export { delay }

export async function redeployStore(providerDeploymentUrl: string): Promise<void> {
  const response = await fetch(providerDeploymentUrl, { method: 'POST' })
  if (!response.ok) {
    throw new Error(`Redeploy failed: ${response.status}`)
  }
}

export async function clearStoreCache(domain: string): Promise<void> {
  const base = domain.endsWith('/') ? domain.slice(0, -1) : domain
  const response = await fetch(`${base}/api/revalidate`, { method: 'POST' })
  if (!response.ok) {
    throw new Error(`Clear cache failed: ${response.status}`)
  }
}

const ACTION_CONCURRENCY = 10

export type StoreActionError = {
  storeId: string
  storeName: string
  message: string
}

export type StoreActionResult = {
  succeeded: number
  failed: number
  errors: StoreActionError[]
}

export type StoreActionProgressEvent = {
  store: StoreWithDeployStatus
  ok: boolean
  error?: string
}

export type StoreActionProgressCallback = (event: StoreActionProgressEvent) => void

function resolveActiveTargets(
  stores: StoreWithDeployStatus[],
  storeId?: string,
): StoreWithDeployStatus[] {
  const active = stores.filter((s) => s.isActive)
  if (!storeId) return active
  const one = active.find((s) => s.id === storeId)
  return one ? [one] : []
}

/** POST provider_deployment_url for one or all active stores. */
export async function executeWebhook(
  stores: StoreWithDeployStatus[],
  storeId?: string,
  onProgress?: StoreActionProgressCallback,
): Promise<StoreActionResult> {
  const targets = resolveActiveTargets(stores, storeId)
  const result: StoreActionResult = { succeeded: 0, failed: 0, errors: [] }

  await runWithConcurrency(targets, ACTION_CONCURRENCY, async (store) => {
    try {
      await redeployStore(store.provider_deployment_url)
      result.succeeded += 1
      onProgress?.({ store, ok: true })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      result.failed += 1
      result.errors.push({
        storeId: store.id,
        storeName: store.name,
        message,
      })
      onProgress?.({ store, ok: false, error: message })
    }
  })

  return result
}

/** POST /api/revalidate for one or all active stores. */
export async function clearCache(
  stores: StoreWithDeployStatus[],
  storeId?: string,
  onProgress?: StoreActionProgressCallback,
): Promise<StoreActionResult> {
  const targets = resolveActiveTargets(stores, storeId)
  const result: StoreActionResult = { succeeded: 0, failed: 0, errors: [] }

  await runWithConcurrency(targets, ACTION_CONCURRENCY, async (store) => {
    try {
      await clearStoreCache(store.domain)
      result.succeeded += 1
      onProgress?.({ store, ok: true })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      result.failed += 1
      result.errors.push({
        storeId: store.id,
        storeName: store.name,
        message,
      })
      onProgress?.({ store, ok: false, error: message })
    }
  })

  return result
}
